import qs from 'qs'

import { RestClient } from '@affstore-ds/restclient'
import { ResponseErrorCallbackType } from '@affstore-ds/restclient/dist/restclient/types'

import { TrackerTypesOperations } from './api/types'

import { getApiTrackerAffiliate, postApiTrackerAffiliateAction } from './api'
import { CABINET_OPENED_EVENT_NAME, DEFAULT_COOKIE_PATH } from './constants'
import { Cookies } from './helpers'

interface ParametersInterface {
  [key: string]: string
}

type TrackedURLParametersType = 'ref_id' | 'afftrack' | 'sourceMarker' | 'source' | 'keyword'
type WrittenCookiesType = 'ref_id' | 'afftrack' | 'source' | 'keyword'

const urlToTrackerMap: Partial<Record<TrackedURLParametersType, string>> = {
  afftrack: 'aff_track',
  ref_id: 'aff_id',
  sourceMarker: 'source',
}

const COOKIE_EXPIRY = 86_400 * 30
const DELETE_COOKIE_EXPIRY = -99_999_999

const TRACKED_URL_PARAMS: TrackedURLParametersType[] = ['ref_id', 'afftrack', 'sourceMarker', 'source', 'keyword']

const WRITTEN_COOKIES: WrittenCookiesType[] = ['ref_id', 'afftrack', 'source', 'keyword']

const parameterToCookieMap: ParametersInterface = {
  sourceMarker: 'source',
}

const isBrowser = typeof window !== 'undefined'

const parametersToObject = (): ParametersInterface => {
  const searchParametersString = isBrowser ? window.location.search : ''
  const searchParameters = new URLSearchParams(searchParametersString)
  const parametersEntries = searchParameters.entries()
  const result: ParametersInterface = {}

  for (const [key, value] of parametersEntries) {
    result[key] = value
  }

  return result
}

const parameters = parametersToObject()

const validateParameters = () => {
  if (typeof parameters !== 'object' || parameters === null) {
    return false
  }

  if (parameters['ref_id']) {
    return typeof parameters['ref_id'] === 'string' && /^\d+$/.test(parameters['ref_id'])
  }

  return true
}

const filterAndRenameParameters = (): Map<string, string> =>
  TRACKED_URL_PARAMS.reduce((accumulator, key) => {
    const value = parameters[key]

    if (value !== undefined && value !== null) {
      const cookieKey = parameterToCookieMap[key] || key

      accumulator.set(cookieKey, value)
    }

    return accumulator
  }, new Map())

const filteredParameters = filterAndRenameParameters()

export const setErrorCallback = (errorCallback: ResponseErrorCallbackType) =>
  RestClient.setResponseErrorCallback(errorCallback)

export const getWrittenCookies = () =>
  (isBrowser
    ? WRITTEN_COOKIES.map((property) =>
        Cookies.getCookie(property) ? { [`${property}`]: Cookies.getCookie(property) } : false,
      )
        .filter(Boolean)
        .reduce((accumulator, current) => Object.assign(accumulator, current), {})
    : {}) as Partial<Record<WrittenCookiesType, string>>

export const setWrittenCookies = (domain: string, path: string = DEFAULT_COOKIE_PATH) => {
  if (filteredParameters.size <= 0 || !validateParameters()) {
    return
  }

  for (const key of WRITTEN_COOKIES) {
    const value = filteredParameters.get(key)

    Cookies.setCookie(key, value || '', {
      domain,
      path,
      expires: value ? COOKIE_EXPIRY : DELETE_COOKIE_EXPIRY,
    })
  }
}

export const checkParametersAndSendHit = (siteId: number, apiDomain: string) => {
  const parameters = qs.parse(window.location.search, { ignoreQueryPrefix: true })

  const parametersToSend = TRACKED_URL_PARAMS.reduce<
    TrackerTypesOperations['GetApiTrackerAffiliate']['parameters']['query']
  >(
    (result, key) => {
      const value = parameters[key]

      if (value === undefined) {
        return result
      }

      const mappedKey = urlToTrackerMap[key] || key

      return { ...result, [mappedKey]: value }
    },
    { site_id: siteId },
  )

  try {
    getApiTrackerAffiliate(parametersToSend, apiDomain)
  } catch (error) {
    console.error(error)
  }
}

export const sendCabinetOpenedEvent = (apiDomain: string) => {
  postApiTrackerAffiliateAction(
    {
      name: CABINET_OPENED_EVENT_NAME,
    },
    apiDomain,
  )
}

if (isBrowser) {
  window.affiliateFrontendAnalyticsTracker = {
    checkParametersAndSendHit,
    getWrittenCookies,
    sendCabinetOpenedEvent,
    setWrittenCookies,
  }
  window.dispatchEvent(new Event('affiliateFrontendAnalyticsTracker:ready'))
}
